// Web one-way mirror of the syncable state to Firestore via the Firebase JS
// SDK (the `firebase` package). No-ops if the web app isn't configured
// (EXPO_PUBLIC_FIREBASE_* env vars).

import { deleteDoc, doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';

import { getFirebaseWebApp } from '../firebaseWeb';
import { SyncableState } from './merge';

export const writeUserState = async (uid: string, state: SyncableState): Promise<void> => {
  const app = getFirebaseWebApp();
  if (!app || !uid) return;
  try {
    // `hasAlerts` lets the scheduled notifier query only users with alerts
    // instead of scanning the whole users collection hourly. See native store.
    const payload = { ...state, hasAlerts: (state.alerts?.length ?? 0) > 0 };
    await setDoc(doc(getFirestore(app), 'users', uid), payload as unknown as Record<string, unknown>, {
      merge: true,
    });
  } catch {
    /* best-effort backup */
  }
};

// Read the user's backed-up state. Returns null ONLY when there's genuinely no
// remote copy — no web app configured, no uid, or no document yet (new
// account). A real read FAILURE (network/permission) is allowed to throw so the
// caller aborts the sign-in sync instead of treating it as "no remote" and
// overwriting the cloud backup with (possibly empty) local state. See native.
export const readUserState = async (uid: string): Promise<SyncableState | null> => {
  const app = getFirebaseWebApp();
  if (!app || !uid) return null;
  const snap = await getDoc(doc(getFirestore(app), 'users', uid));
  if (!snap.exists()) return null;
  return (snap.data() as SyncableState) ?? null;
};

// Delete the user's backed-up document (account deletion). Throws on failure so
// the caller can abort before removing the auth account. See native store.
export const deleteUserState = async (uid: string): Promise<void> => {
  const app = getFirebaseWebApp();
  if (!app || !uid) return;
  await deleteDoc(doc(getFirestore(app), 'users', uid));
};

export default { writeUserState, readUserState, deleteUserState };
